"use client";

import { Button } from "@heroui/button";
import { Tooltip } from "@heroui/tooltip";
import { useState, useEffect } from "react";
import { useTranslations } from "next-intl";
import { FaLink, FaCheck, FaCopy } from "react-icons/fa";

import { siteConfig } from "@/config/site";

interface SocialShareProps {
  // Content props (with smart defaults)
  url?: string; // Optional - defaults to current URL via window.location.href
  title?: string; // Optional - defaults to document.title
  description?: string;
  hashtags?: string[]; // Optional - manually provided hashtags

  // UI customization props
  className?: string;
  size?: "sm" | "md" | "lg";
  variant?: "flat" | "bordered" | "solid" | "ghost";
  showLabels?: boolean;

  // Section layout props
  sectionTitle?: string;
  showSection?: boolean;
  sectionClassName?: string;

  // Share text - required, caller must provide
  shareText: string; // Pre-translated share text message
}

// Clean and validate hashtags
function cleanHashtags(hashtags: string[]): string[] {
  return hashtags
    .map(tag => tag.toLowerCase().replace(/[^a-z0-9]/g, "")) // Remove special chars, lowercase
    .filter(tag => tag.length > 0 && tag.length <= 20) // Valid length
    .filter((tag, index, arr) => arr.indexOf(tag) === index) // Remove duplicates
    .slice(0, 5); // Limit to 5 hashtags for better social media performance
}

export function SocialShare({
  url,
  title,
  description = "",
  hashtags = [],
  className = "",
  size = "lg",
  variant = "flat",
  showLabels = false,
  // Section props
  sectionTitle,
  showSection = false,
  sectionClassName = "",
  // Share text - required
  shareText,
}: SocialShareProps) {
  const t = useTranslations("SocialShare");
  const [copied, setCopied] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Handle hydration and mobile detection
  useEffect(() => {
    // Use a timeout to avoid synchronous state updates
    const timer = setTimeout(() => {
      setMounted(true);
      // Detect if user is on mobile device
      const checkMobile = () => {
        const userAgent =
          navigator.userAgent || navigator.vendor || (window as any).opera;

        return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
          userAgent.toLowerCase()
        );
      };

      setIsMobile(checkMobile());
    }, 0);

    return () => clearTimeout(timer);
  }, []);

  // Get actual values after hydration with better defaults
  const actualUrl = mounted ? url || window.location.href : url || "";
  const actualTitle = mounted ? title || document.title : title || "";
  // Get Twitter handle from site config
  const twitterSocial = siteConfig.socials.find(
    social =>
      social.label.toLowerCase().includes("twitter") ||
      social.label.toLowerCase().includes("x")
  );
  const via = twitterSocial?.href.split("/").pop() || "faxify_app"; // Extract handle from URL

  // Clean and process hashtags
  const cleanedHashtags = cleanHashtags(hashtags);

  // Generate share text by combining the provided share text with title
  const finalShareText = `${shareText}: ${actualTitle}`;

  const shareData = {
    url: encodeURIComponent(actualUrl),
    title: encodeURIComponent(actualTitle),
    shareText: encodeURIComponent(finalShareText), // Engaging message + title
    description: encodeURIComponent(description),
    hashtags: cleanedHashtags.join(","),
  };

  // Create social sharing platforms from site config (excluding YouTube)
  // Also exclude Instagram on desktop (no web sharing API available)
  const platforms = siteConfig.socials
    .filter(socialLink => !socialLink.label.toLowerCase().includes("youtube"))
    .filter(socialLink => {
      // Hide Instagram on desktop since it has no web sharing API
      const isInstagram = socialLink.label.toLowerCase().includes("instagram");

      return !(isInstagram && !isMobile);
    })
    .map(socialLink => {
      // Create appropriate share URLs based on the platform
      let shareUrl = socialLink.href;

      // Map social platforms to their sharing URLs
      if (socialLink.label.toLowerCase().includes("facebook")) {
        // Facebook will auto-fetch title and description from URL
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${shareData.url}`;
      } else if (
        socialLink.label.toLowerCase().includes("twitter") ||
        socialLink.label.toLowerCase().includes("x")
      ) {
        // Twitter/X with engaging share text, hashtags, and attribution
        shareUrl = `https://twitter.com/intent/tweet?url=${shareData.url}&text=${shareData.shareText}&via=${via}${cleanedHashtags.length ? `&hashtags=${shareData.hashtags}` : ""}`;
      } else if (socialLink.label.toLowerCase().includes("linkedin")) {
        // LinkedIn will auto-fetch title and description from URL (can't pre-fill text)
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${shareData.url}`;
      } else if (socialLink.label.toLowerCase().includes("youtube")) {
        // YouTube doesn't have direct sharing, so link to the channel
        shareUrl = socialLink.href;
      } else if (socialLink.label.toLowerCase().includes("instagram")) {
        // Instagram: Use deep link on mobile to open in app, otherwise use profile
        // Deep link format: instagram://story-camera or instagram://share
        if (isMobile) {
          // Try to open Instagram app with deep link, fallback to profile
          shareUrl = `instagram://story-camera?url=${shareData.url}`;
        } else {
          // On desktop, just link to profile (no web sharing API available)
          shareUrl = socialLink.href;
        }
      }

      return {
        name: socialLink.label,
        icon: socialLink.icon,
        color: "primary" as const,
        href: shareUrl,
      };
    });

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(actualUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to copy: ", err);
    }
  };

  const handleShare = (platform: string, href: string) => {
    // Track social sharing for analytics
    if (typeof window !== "undefined" && "gtag" in window) {
      window.gtag("event", "share", {
        method: platform.toLowerCase(),
        content_type: "article",
        item_id: url,
      });
    }

    // Open share window
    const width = 600;
    const height = 400;
    const left = (window.innerWidth - width) / 2;
    const top = (window.innerHeight - height) / 2;

    window.open(
      href,
      `share-${platform}`,
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,location=no,status=no`
    );
  };

  // Use native Web Share API if available
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: actualTitle,
          text: finalShareText, // Use the provided share text
          url: actualUrl,
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error sharing:", err);
      }
    }
  };

  // Don't render until mounted to prevent hydration issues
  if (!mounted) {
    return showSection ? (
      <div className={`${sectionClassName}`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="text-sm font-medium text-default-700 mb-2">
              {sectionTitle || t("defaultTitle")}
            </h3>
            <div className="flex flex-wrap gap-2">
              {/* Loading placeholder */}
              <div className="h-10 w-10 bg-default-200 rounded-full animate-pulse" />
              <div className="h-10 w-10 bg-default-200 rounded-full animate-pulse" />
              <div className="h-10 w-10 bg-default-200 rounded-full animate-pulse" />
              <div className="h-10 w-10 bg-default-200 rounded-full animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    ) : (
      <div className={`flex flex-wrap gap-2 ${className}`}>
        {/* Loading placeholder */}
        <div className="h-10 w-10 bg-default-200 rounded-full animate-pulse" />
        <div className="h-10 w-10 bg-default-200 rounded-full animate-pulse" />
        <div className="h-10 w-10 bg-default-200 rounded-full animate-pulse" />
        <div className="h-10 w-10 bg-default-200 rounded-full animate-pulse" />
      </div>
    );
  }

  const shareButtons = (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {/* Native Share API (mobile) */}
      {typeof navigator !== "undefined" && "share" in navigator && (
        <Tooltip content={t("shareViaSystem")}>
          <Button
            aria-label={t("shareViaSystem")}
            color="secondary"
            isIconOnly={!showLabels}
            radius="full"
            size={size}
            startContent={!showLabels ? <FaLink /> : undefined}
            variant={variant}
            onPress={handleNativeShare}
          >
            {showLabels && (
              <>
                <FaLink /> {t("shareLabel")}
              </>
            )}
          </Button>
        </Tooltip>
      )}

      {/* Social platforms */}
      {platforms.map(platform => {
        // Determine appropriate tooltip text based on platform
        // Instagram on mobile uses deep link to share in app
        // YouTube just links to channel (no sharing API)
        const isInstagramMobile =
          platform.name.toLowerCase().includes("instagram") && isMobile;
        const isYouTube = platform.name.toLowerCase().includes("youtube");

        const tooltipText = isYouTube
          ? `Visit ${platform.name}`
          : isInstagramMobile
            ? `${t("shareOn")} ${platform.name}` // Share via Instagram app
            : `${t("shareOn")} ${platform.name}`; // Standard web sharing

        return (
          <Tooltip key={platform.name} content={tooltipText}>
            <Button
              aria-label={tooltipText}
              color="primary"
              isIconOnly={!showLabels}
              radius="full"
              size={size}
              startContent={!showLabels ? <platform.icon /> : undefined}
              variant={variant}
              onPress={() => handleShare(platform.name, platform.href)}
            >
              {showLabels && (
                <>
                  <platform.icon /> {platform.name}
                </>
              )}
            </Button>
          </Tooltip>
        );
      })}

      {/* Copy link */}
      <Tooltip content={copied ? t("copied") : t("copyLink")}>
        <Button
          aria-label={copied ? t("copied") : t("copyLink")}
          color={copied ? "success" : "default"}
          isIconOnly={!showLabels}
          radius="full"
          size={size}
          startContent={
            !showLabels ? copied ? <FaCheck /> : <FaCopy /> : undefined
          }
          variant={variant}
          onPress={handleCopyLink}
        >
          {showLabels && (
            <>
              {copied ? <FaCheck /> : <FaCopy />}
              {copied ? t("copied") : t("copyLinkLabel")}
            </>
          )}
        </Button>
      </Tooltip>
    </div>
  );

  // If showSection is true, wrap in section styling
  if (showSection) {
    return (
      <div className={`${sectionClassName}`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="text-sm font-medium text-default-700 mb-2">
              {sectionTitle || t("defaultTitle")}
            </h3>
            {shareButtons}
          </div>
        </div>
      </div>
    );
  }

  // Return just the buttons if not in section mode
  return shareButtons;
}

// Compact social share component for cards/lists
export function SocialShareCompact({
  url,
  title,
  shareText,
  className = "",
}: Pick<SocialShareProps, "url" | "title" | "shareText" | "className">) {
  return (
    <SocialShare
      className={`justify-center ${className}`}
      shareText={shareText}
      size="sm"
      title={title}
      url={url}
      variant="flat"
    />
  );
}

// Enhanced social meta tags component
export function SocialMetaTags({
  title,
  description,
  image,
  url,
  type = "website",
  siteName,
  locale = "en_US",
  twitterCard = "summary_large_image",
  twitterSite,
  twitterCreator,
}: {
  title: string;
  description: string;
  image?: string;
  url?: string;
  type?: "website" | "article" | "profile";
  siteName?: string;
  locale?: string;
  twitterCard?: "summary" | "summary_large_image" | "app" | "player";
  twitterSite?: string;
  twitterCreator?: string;
}) {
  return (
    <>
      {/* Open Graph */}
      <meta content={type} property="og:type" />
      <meta content={title} property="og:title" />
      <meta content={description} property="og:description" />
      {url && <meta content={url} property="og:url" />}
      {image && <meta content={image} property="og:image" />}
      {siteName && <meta content={siteName} property="og:site_name" />}
      <meta content={locale} property="og:locale" />

      {/* Twitter Card */}
      <meta content={twitterCard} name="twitter:card" />
      <meta content={title} name="twitter:title" />
      <meta content={description} name="twitter:description" />
      {image && <meta content={image} name="twitter:image" />}
      {twitterSite && <meta content={twitterSite} name="twitter:site" />}
      {twitterCreator && (
        <meta content={twitterCreator} name="twitter:creator" />
      )}

      {/* Additional social meta */}
      <meta content={description} name="description" />
      <meta content="index,follow" name="robots" />
      <meta content="index,follow" name="googlebot" />
    </>
  );
}
